import { supabaseRealtime } from "@/lib/supabase";
import { useEffect, useRef, useState } from "react";

export type BroadcastSubscriptionStatus =
  | "IDLE"
  | "SUBSCRIBING"
  | "SUBSCRIBED"
  | "TIMED_OUT"
  | "CHANNEL_ERROR"
  | "CLOSED";

export const useSupabaseBroadcast = ({
  channelName,
  event,
  callbackFn,
  debug = false,
}: {
  channelName: string;
  event: string;
  callbackFn: (payload: unknown) => void;
  debug?: boolean;
}) => {
  const [status, setStatus] = useState<BroadcastSubscriptionStatus>("IDLE");
  const [error, setError] = useState<Error | null>(null);
  // Use a ref to store the latest callback without triggering re-subscriptions
  const callbackRef = useRef(callbackFn);

  // Keep the ref updated with the latest callback
  useEffect(() => {
    callbackRef.current = callbackFn;
  }, [callbackFn]);

  useEffect(() => {
    if (!channelName) {
      setStatus("IDLE");
      setError(null);
      return;
    }

    const channel = supabaseRealtime.channel(channelName);
    setStatus("SUBSCRIBING");
    setError(null);
    console.log("subscribing to channel", channelName);
    channel
      .on("broadcast", { event }, (payload) => {
        if (debug) {
          console.log("broadcast payload", channelName, payload);
        }
        // Use the ref to always call the latest callback
        callbackRef.current(payload);
      })
      .on("system", { event: "*" }, (payload) => {
        if (debug) {
          console.log("system", payload);
        }
      })
      .subscribe((status, err) => {
        setStatus(status as BroadcastSubscriptionStatus);
        if (debug) {
          console.log("status", status);
        }
        if (err) {
          setError(err);
          console.log("error", err);
        } else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
          setError(new Error(`Realtime channel ${status.toLowerCase()}`));
        }
      });

    return () => {
      void channel.unsubscribe();
    };
  }, [channelName, event, debug]);

  return {
    status,
    error,
    isSubscribed: status === "SUBSCRIBED",
    hasSubscriptionError: status === "CHANNEL_ERROR" || status === "TIMED_OUT",
  };
};
